import {
  BaseTable,
  Card,
  CommandButton,
  DateInput,
  Loading,
  PaginationData,
  PermissionLink,
  Select
} from "components"
import { DeleteItem } from "../DeleteItem"
import { IoSendSharp } from "react-icons/io5"
import moment from "moment"
import { convertNumber, default_pagination_value } from "utils"
import { default_option, delivery_option, usePurchaseList } from "../../utils"
import { useExportPdf } from "./utils/hooks"
import { exportExcel } from "./utils/functions"
import { useEffect, useState } from "react"
import { PaginationState } from "types"
import { FormType } from "pages/Purchase/Register/types"
import { PERMISION } from "security"

export function ByDateRange(): JSX.Element {
  const [filter, setFilter] = useState<FormType>({
    Approval: "4",
    DateEnd: moment().endOf("month").format("YYYY-MM-DD"),
    DateFrom: moment().startOf("year").format("YYYY-MM-DD"),
    Delivered: "0"
  })
  const [pagination, setPagination] = useState<PaginationState>(
    default_pagination_value
  )
  const { data, isLoading, refetch } = usePurchaseList({
    Approval: filter?.Approval,
    DateEnd: filter?.DateEnd,
    DateFrom: filter?.DateFrom,
    Delivered: filter?.Delivered,
    total: pagination.total || 0,
    page: pagination.page || 10,
    limit: pagination.limit || 10,
    pages: pagination.pages || 1
  })
  const totalPayment = data?.payload?.reduce(
    (total, item) => total + item.total_payment,
    0
  )
  const totalBalance = data?.payload?.reduce(
    (total, item) => total + item.balance_owing,
    0
  )

  const totalQtyOrder = data?.payload?.reduce(
    (total, item) => total + item.qty_order,
    0
  )

  const totalQtyDelivered = data?.payload?.reduce(
    (total, item) => total + item.qty_delivered,
    0
  )


  const exportDateFormat = `${moment(filter.DateFrom).format(
    "DD/MM/YYYY"
  )} - ${moment(filter.DateEnd).format("DD/MM/YYYY")}`
  const exportPdf = useExportPdf({
    data: data?.payload,
    dateFilter: exportDateFormat
  })

  useEffect(() => {
    refetch()
    //eslint-disable-next-line
  }, [filter, pagination])

  return (
    <section className="container flex flex-col gap-3">
      <section className="mt-2">
        <Card>
          <Card.Body>
            <section className="lg:flex overflow-x-auto">
              <div className="flex items-center w-[284px] lg:w-[250px]">
                <div className="label-text pb-1 font-bold pe-2 lg:w-[145px] w-[350px] uppercase">
                  START DATE
                </div>
                <DateInput
                  className="!w-[150px]"
                  selected={moment(filter.DateFrom).toDate()}
                  onChange={(date) =>
                    setFilter({
                      ...filter,
                      DateFrom: moment(date).format("YYYY-MM-DD")
                    })
                  }
                />
              </div>
              <div className="flex items-center w-[284px] lg:w-[250px] mt-2 lg:mt-0">
                <div className="lg:mt-0 lg:px-2 pb-1 label-text font-bold lg:w-[110px] !w-[550px] uppercase">
                  END DATE
                </div>
                <DateInput
                  className="!w-[150px]"
                  selected={moment(filter.DateEnd).toDate()}
                  onChange={(date) =>
                    setFilter({
                      ...filter,
                      DateEnd: moment(date).format("YYYY-MM-DD")
                    })
                  }
                />
              </div>
              <div className="flex items-center ml-1 mt-2 lg:mt-0">
                <div className="lg:mt-0 lg:px-2 pb-1 label-text font-bold w-[142px] lg:w-full">
                  APPROVAL
                </div>
                <Select
                  className="w-[150px]"
                  value={default_option.find(
                    (item) => item.value === filter.Approval
                  )}
                  options={default_option}
                  onChange={(res) => {
                    if (res) {
                      setFilter({ ...filter, Approval: res.value })
                    }
                  }}
                />
              </div>
              <div className="flex items-center ml-1 mt-2 lg:mt-0">
                <div className="lg:mt-0 lg:px-2 pb-1 label-text font-bold w-[142px] lg:w-full">
                  DELIVERED
                </div>
                <Select
                  className="w-[150px]"
                  value={delivery_option.find(
                    (item) => item.value === filter.Delivered
                  )}
                  options={delivery_option}
                  onChange={(res) => {
                    if (res) {
                      setFilter({ ...filter, Delivered: res.value })
                    }
                  }}
                />
              </div>
            </section>
          </Card.Body>
        </Card>
      </section>

      <section>
        <Card>
          <Card.Body>
            <div className="overflow-x-auto max-h-[calc(100vh-570px)]">
              <BaseTable>
                <thead className="top-0 z-1 !bg-black !text-white">
                  <tr>
                    <th rowSpan={2}>NO</th>
                    <th rowSpan={2}>PO ID#</th>
                    <th rowSpan={2}>DATE</th>
                    <th rowSpan={2}>VENDOR</th>
                    <th colSpan={2}>STATUS</th>
                    <th rowSpan={2}>
                      QTY
                      <br />
                      ORDER
                    </th>
                    <th rowSpan={2}>
                      QTY
                      <br />
                      DELIVERED
                    </th>
                    <th rowSpan={2}>
                      QTY
                      <br />
                      BACKORDER
                    </th>
                    <th rowSpan={2}>
                      TOTAL
                      <br />
                      PURCHASE
                    </th>
                    <th rowSpan={2}>
                      TOTAL
                      <br />
                      PAYMENT
                    </th>
                    <th rowSpan={2}>
                      BALANCE
                      <br />
                      OWING
                    </th>
                    <th rowSpan={2}>ACTION</th>
                  </tr>
                  <tr>
                    <th>APPROVAL</th>
                    <th>DELIVERED</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading || data?.payload?.length === 0 ? (
                    <tr>
                      <td colSpan={13}>
                        <Loading
                          loading={isLoading}
                          errorText="No data available"
                        />
                      </td>
                    </tr>
                  ) : (
                    data?.payload?.map((item, key) => (
                      <tr key={key}>
                        <td>{key + 1}</td>
                        <td>{item.trx_code}</td>
                        <td>
                          {moment(item.transaction_date).format("DD/MM/YYYY")}
                        </td>
                        <td>{item.vendor_name}</td>
                        <td>{item.approval}</td>
                        <td>{item.delivery}</td>
                        <td className="text-center">
                          {item.qty_order
                            ? convertNumber(item.qty_order).intoNormalAmount
                            : "-"}
                        </td>
                        <td className="text-center">
                          {item.qty_delivered
                            ? convertNumber(item.qty_delivered).intoNormalAmount
                            : "-"}
                        </td>
                        <td className="text-center">
                          {item.qty_back_order
                            ? convertNumber(item.qty_back_order)
                                .intoNormalAmount
                            : "-"}
                        </td>
                        <td className="text-right">
                          {item.total_purchase
                            ? convertNumber(item.total_purchase).intoCurrency
                            : "-"}
                        </td>
                        <td className="text-right">
                          {item.total_payment
                            ? convertNumber(item.total_payment).intoCurrency
                            : "-"}
                        </td>
                        <td className="text-right">
                          {item.balance_owing
                            ? convertNumber(item.balance_owing).intoCurrency
                            : "-"}
                        </td>
                        <td>
                          <section className="flex items-center gap-2">
                            <PermissionLink
                              permission={PERMISION.PURCHASES.REGISTER.BY_DATE_RANGE.UPDATE}
                              to={`/purchase/transaction/purchase-order/${item.purchase_order_id}`}
                              className="mt-1">
                              <IoSendSharp />
                            </PermissionLink>
                            {item.approval_status === 1 && (
                              <DeleteItem
                                item={{
                                  purchase_order_id:
                                    item.purchase_order_id.toString(),
                                  transaction_date: item.transaction_date,
                                  trx_code: item.trx_code,
                                  vendor_name: item.vendor_name
                                }}
                                onSuccess={refetch}
                              />
                            )}
                          </section>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </BaseTable>
              <div className="mt-3">
                <PaginationData
                  data={data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </div>
            </div>

          </Card.Body>
        </Card>
      </section>

      <Card>
        <Card.Body className="flex sm:flex-row flex-col justify-between sm:items-center">
          <section className="flex flex-wrap gap-3">
            <CommandButton
              actiontype="export"
              onClick={() =>
                exportExcel({
                  data: data?.payload,
                  dateFilter: exportDateFormat
                })
              }
            />
            <CommandButton
              actiontype="pdf"
              onClick={exportPdf.handleExportPdf}
            />
          </section>

          <section className="flex flex-col">
            <div className="label-text font-bold flex justify-end text-right">
              <div>
                <div>SUMMARY QTY ORDER</div>
                <div>SUMMARY QTY DELIVERED</div>
                <div>SUMMARY PAYMENT</div>
                <div>SUMMARY BALANCE</div>
              </div>
              <div className="mx-2">
                <div>:</div>
                <div>:</div>
                <div>:</div>
                <div>:</div>
              </div>
              <div>
                <div>
                  {totalQtyOrder
                    ? convertNumber(totalQtyOrder)?.intoCurrency
                    : "0"}
                </div>
                <div>
                  {totalQtyDelivered
                    ? convertNumber(totalQtyDelivered)?.intoCurrency
                    : "0"}
                </div>
                <div>
                  {totalPayment
                    ? convertNumber(totalPayment)?.intoCurrency
                    : "0"}
                </div>
                <div>
                  {totalBalance
                    ? convertNumber(totalBalance)?.intoCurrency
                    : "0"}
                </div>
              </div>
            </div>
          </section>
        </Card.Body>
      </Card>
    </section>
  )
}
